import { useApiQuery } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';

// API wrapper from backend
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// GET /api/v1/view/clients/{clientId}/modules
export type ModulesApiResponse = ApiResponse<ModulesData>;

// When no config is set, data will be {} with a message
export type ModulesData = ModulesConfig | Record<string, never>;

export interface ModulesConfig {
  id: string;
  modules: ModuleItem[];
}

export type ModuleItem = EntityLeaf | AppLeaf | FrontendRouteLeaf | NavNode;

export interface BaseLeaf {
  id?: string;
  label?: string;
  order?: number;
  icon?: string;
}

export interface EntityLeaf extends BaseLeaf {
  type: 'ENTITY';
  entityName: string;
}

export interface AppLeaf extends BaseLeaf {
  type: 'APP';
  appId: string;
  appOverrides?: {
    name?: string;
    link?: string; // URI
    description?: string;
  };
}

export interface FrontendRouteLeaf extends BaseLeaf {
  type: 'FRONTEND_ROUTE';
  route: string;
}

export interface NavNode {
  id: string;
  key: string;
  label: string;
  icon?: string;
  order?: number;
  items: ModuleItem[];
}

export function useModules(clientId: string) {
  const { showApiError } = useToast();
  return useApiQuery<ModulesApiResponse>({
    key: ['modules', clientId],
    endpointKey: 'modules.list',
    params: { clientId },
    config: {
      staleTime: 5 * 60 * 1000,
      enabled: Boolean(clientId),
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
  });
}
